import { useState } from 'react';
import { useClusterApi } from '../../api/ClusterApiProvider';
import { useAsync } from '../../api/useAsync';
import type { AuditEvent } from '../../api/types';
import { AgoUnix } from '../../ui/Ago';
import { ActorMark } from './ActorMark';
import { actionWord } from './auditAction';
import { CLASSES, versionBehindRefusal } from './auditClass';
import type { ClassFilter } from './auditClass';
import { AuditDrawer } from './AuditDrawer';
import { outcomeTone } from './auditOutcome';
import { StatusBadge } from '../../ui/StatusBadge';
import { useI18n } from '../../i18n/useI18n';
import { Card, Loading, PageHead } from '../../ui/primitives';
import { FilterBar, SearchInput, SegmentedFilter } from '../../ui/form';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { DataTable } from '../../ui/DataTable';

/** Stable identity, so the table is not handed a new array every render. */
const NO_ROWS: AuditEvent[] = [];

/** One screen of rows. The API holds anything larger to its own ceiling. */
const PAGE = 100;

/**
 * This cluster's audit trail — who did what, to what, and how it ended.
 *
 * **Read through the cluster, and recorded there.** The rows live in a database
 * the control plane created and could open directly; it asks the cluster
 * instead, so the tenancy's own policy decides the read and the tenancy's own
 * chain records it. gdsgate writes a row for a chain read whether it allows or
 * refuses, exactly so that no way of reading the log exists that does not
 * appear in it — and a console reading around the back would have been that
 * way.
 *
 * Three things this screen says rather than implies:
 *
 * * **opening it is an entry.** Somebody should learn that from the page, not
 *   from finding their own name on the trail afterwards;
 * * **the answer is not a proof.** A chain that had been rewritten would answer
 *   just as smoothly; what settles it is `verify-audit` against the database,
 *   which no browser can reach;
 * * **a claimed operator is a claim.** Every column but that one is what the
 *   cluster itself checked. Rendering the two alike would put an unverified
 *   name in a row of verified ones.
 *
 * The class tabs narrow at the cluster, so switching one asks again; the name
 * box narrows the page already read, so typing in it costs nothing and cannot
 * reach past what was fetched. That difference is why the empty states are two
 * and not one.
 *
 * And because they narrow at the cluster, a tab can name a class the tenancy's
 * own gdsgate has never heard of — the console serves several versions at once
 * and learns which is which by asking. Such a read comes back refused, whole,
 * under one of two codes: see `versionBehindRefusal` for why the tab stays
 * anyway and which sentence each of them earns.
 */
export function AuditScreen() {
  const api = useClusterApi();
  const { t } = useI18n();

  const [klass, setKlass] = useState<ClassFilter>('all');
  const [who, setWho] = useState('');
  const [opened, setOpened] = useState<AuditEvent | null>(null);

  const events = useAsync(
    (signal) => api.listAudit(klass === 'all' ? { limit: PAGE } : { class: klass, limit: PAGE }, signal),
    `audit:${klass}`,
  );

  const all: AuditEvent[] = events.status === 'ready' ? events.value : NO_ROWS;
  // Set when the class being read is younger than some of the clusters this
  // console serves and the read came back refused. It carries the words as well
  // as the version, because a cluster that named the word itself is stated and
  // anything else is a supposition — and which of the two it is was decided
  // there rather than here.
  const behind = events.status === 'error' ? versionBehindRefusal(klass, events.error) : undefined;
  const needle = who.trim().toLowerCase();
  const rows =
    needle === '' ? all : all.filter((row) => row.actor.toLowerCase().includes(needle));

  return (
    <div className="content-inner">
      <PageHead title={t('audit.title')} sub={t('audit.subtitle')} />

      <div style={{ marginBottom: 18 }}>
        <Alert tone="warn" title={t('audit.read.title')}>
          <p>{t('audit.read.body')}</p>
        </Alert>
      </div>

      <FilterBar
        filters={
          <SegmentedFilter<ClassFilter>
            legend={t('audit.filter.label')}
            value={klass}
            options={CLASSES.map((value) => ({
              value,
              label: value === 'all' ? t('audit.filter.all') : t(`audit.filter.${value}`),
            }))}
            onChange={setKlass}
          />
        }
        search={
          <SearchInput
            label={t('audit.actor.label')}
            placeholder={t('audit.actor.placeholder')}
            value={who}
            onChange={setWho}
          />
        }
      />

      <Card>
        {events.status === 'loading' ? (
          <Loading />
        ) : events.status === 'error' ? (
          <ErrorAlert
            error={events.error}
            action={
              behind !== undefined && (
                <p style={{ marginTop: 8 }}>{t(behind.key, { version: behind.version })}</p>
              )
            }
          />
        ) : (
          <DataTable
            caption={t('audit.title')}
            rows={rows}
            // What the page held before the name box narrowed it: without it an
            // empty result would read as an empty trail.
            total={all.length}
            // The schema action is in here because without it a current
            // cluster's decisions all carry one action, `policy.decision`, and
            // two of them in the same second on the same resource by the same
            // person — a connect and the first query behind it — would be one
            // key twice.
            rowKey={(row) =>
              `${String(row.at)}-${row.action}-${row.cedar_action ?? ''}-${row.actor}-${row.target}`
            }
            onRowClick={(row) => {
              setOpened(row);
            }}
            empty={{ title: t('audit.none'), body: t('audit.none.body') }}
            noMatch={{ title: t('audit.noMatch'), body: t('audit.noMatch.body') }}
            columns={[
              {
                key: 'when',
                header: t('audit.col.when'),
                cell: (row) => <AgoUnix at={row.at} />,
              },
              {
                // The name the cluster verified, and what sort of thing it is.
                // What a *caller asserted* about who was behind an
                // administrative call — `claimed_operator` — is not here: it is
                // a different claim about a different half of the row, and
                // putting it under the verified name in the same column made
                // every row two lines tall to say something about a few of
                // them. It is in the panel, labelled for what it is.
                key: 'actor',
                header: t('audit.col.actor'),
                cell: (row) => <ActorMark actor={row.actor} />,
              },
              {
                key: 'action',
                header: t('audit.col.action'),
                // The cluster's own word, printed as it arrived. Not a fixed
                // set: gdsgate renamed five session actions into `connect` and
                // will rename others, and a chain keeps what it already wrote —
                // so `sshConnect` and `connect` both turn up, from either side
                // of that day. A table holding its own list of the words would
                // show a blank for the older half of a tenant's history.
                //
                // For a decision the word is the schema action rather than the
                // event name, or this column would read `policy.decision` all
                // the way down the page that exists to show decisions. The code
                // is not here beside it: it is what a row is quoted by, not
                // what a table is scanned by, and it is in the panel.
                cell: (row) => <span className="chip">{actionWord(row)}</span>,
              },
              {
                key: 'target',
                header: t('audit.col.target'),
                cell: (row) => row.target,
                mono: true,
                hideSm: true,
              },
              {
                key: 'outcome',
                header: t('audit.col.outcome'),
                // The one column of this table somebody scans for. A word plus
                // a glyph plus a hue, like every other state in the console.
                cell: (row) => <StatusBadge tone={outcomeTone(row.outcome)} label={row.outcome} />,
              },
            ]}
          />
        )}
      </Card>

      <div style={{ marginTop: 18 }}>
        <Alert tone="info" title={t('audit.proof.title')}>
          <p>{t('audit.proof.body')}</p>
        </Alert>
      </div>

      {opened !== null && (
        <AuditDrawer
          event={opened}
          events={all}
          onClose={() => {
            setOpened(null);
          }}
        />
      )}
    </div>
  );
}
